import { DEFAULT_LOCALE, getLocaleFromUrl, getWithoutLocale } from 'utils';
import { SEARCH_REDIRECTION_URL } from '../headless/headerSearchEngine';

/**
 * The only schemes a redirect trigger may send the shopper to. The value ends
 * up at `window.location.assign`, and a `javascript:` URL has origin `null`,
 * so the off-origin branch would otherwise pass it straight to that sink.
 */
const NAVIGABLE_PROTOCOLS = ['http:', 'https:'];

/**
 * Prefix the shopper's active locale onto an app-relative path so header-search
 * redirects stay in their locale — /ja-JP/search?q=… rather than the
 * default-locale /search. The default locale is served unprefixed, matching the
 * app's routing convention.
 *
 * The locale is read from the live URL (client-only) rather than from context,
 * so it works the same in the connected box and in the plain form that renders
 * before the engine exists.
 */
export function withActiveLocale(path: string): string {
  const locale = getLocaleFromUrl();
  return locale === DEFAULT_LOCALE ? path : `/${locale}${path}`;
}

/** The locale-correct results URL for a raw, untrimmed query. */
export function searchUrlForQuery(query: string): string {
  return withActiveLocale(
    `${SEARCH_REDIRECTION_URL}?q=${encodeURIComponent(query.trim())}`
  );
}

/**
 * The URL a connected submit should land on: Coveo's redirect URL (the
 * configured /search, or the URL of a query-pipeline redirect trigger) carrying
 * the submitted query, in the shopper's locale.
 *
 * An off-origin trigger URL is returned untouched: prefixing our locale onto
 * someone else's path — or dropping its origin, as reading pathname+search
 * alone would — sends the shopper to a path that doesn't exist here.
 *
 * Anything unparseable or non-navigable falls back to the plain results URL:
 * letting `new URL` throw here would leave `afterRedirection` uncalled and the
 * header stuck holding a redirect it never performs.
 */
export function redirectUrlForQuery(redirectTo: string, query: string): string {
  let url: URL;
  try {
    url = new URL(redirectTo, window.location.origin);
  } catch {
    return searchUrlForQuery(query);
  }
  if (!NAVIGABLE_PROTOCOLS.includes(url.protocol)) {
    return searchUrlForQuery(query);
  }
  const trimmedQuery = query.trim();
  if (trimmedQuery) {
    url.searchParams.set('q', trimmedQuery);
  }
  if (url.origin !== window.location.origin) {
    return url.href;
  }
  return withActiveLocale(`${localelessPath(url.pathname)}${url.search}`);
}

/**
 * The trigger's path with its locale prefix removed, so a trigger configured as
 * /ja-JP/campaign doesn't come back /ja-JP/ja-JP/campaign for a shopper already
 * browsing in Japanese.
 *
 * getWithoutLocale slices a fixed six characters off anything that starts with
 * a supported locale, without checking what follows, so an admin-configured
 * /ja-JPsale turns into a relative 'sale' — which location.assign would resolve
 * against the current page rather than the site root. Keep the original path
 * when stripping leaves something unrooted; a doubled prefix is a wrong page,
 * an unrooted path is a wrong page that depends on where the shopper stood.
 *
 * The query string is appended afterwards rather than passed through the strip,
 * which would turn /ja-JP?promo=1 into the equally relative '?promo=1'.
 */
function localelessPath(pathname: string): string {
  const stripped = getWithoutLocale(pathname);
  if (!stripped.startsWith('/')) {
    return stripped === '' ? '/' : pathname;
  }
  return stripped;
}

/**
 * The query the page is already showing results for, so the header box can
 * display it.
 *
 * The results page seeds its own engine from `q` server-side and the header
 * runs on a separate engine that knows nothing about it, so without this the
 * box the shopper just searched from comes back empty from every search.
 *
 * Takes the route rather than reading `window`, so the header renders the
 * query on the server too — an empty box that fills at hydration is the flash
 * this exists to avoid.
 *
 * Only on the results route: a `q` param on any other page belongs to that
 * page, not to search.
 */
export function activeSearchQuery(
  pathname: string,
  searchParams: Pick<URLSearchParams, 'get'> | null
): string {
  const path = getWithoutLocale(pathname).replace(/\/$/, '');
  if (path !== SEARCH_REDIRECTION_URL) {
    return '';
  }
  return searchParams?.get('q')?.trim() ?? '';
}

/**
 * Leave the page for the results URL.
 *
 * A FULL navigation, never `router.push`: the SSR results page seeds the query
 * from the URL server-side, and during a soft transition its
 * SearchParameterManager can serialize engine state onto the previous page's
 * still-live URL, rewriting history to `<old-path>?q=…` and silently clearing
 * the query (the same finding as useNavigateToSearch and POC commit 3864cbb3).
 */
export function navigateToResults(url: string): void {
  window.location.assign(url);
}
